/**
 * Shared planet name translation utility.
 * Handles Rahu/Ketu ↔ North Node/South Node mapping and language lookups.
 */

/**
 * Translate a planet name using the provided translation map.
 * Handles "North Node (Rahu)" / "South Node (Ketu)" variants from the backend.
 */
export const getPlanetName = (name: string, translations: Record<string, string> | undefined): string => {
    if (!name) return name;

    const lowerName = name.toLowerCase();
    if (lowerName.includes('rahu') || lowerName.includes('north node')) {
        return translations?.Rahu || name;
    }
    if (lowerName.includes('ketu') || lowerName.includes('south node')) {
        return translations?.Ketu || name;
    }
    if (translations?.[name]) {
        return translations[name];
    }
    const capitalizedName = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    if (translations?.[capitalizedName]) {
        return translations[capitalizedName];
    }
    return name;
};

/**
 * Normalize Rahu/Ketu terms in free-text descriptions (English only).
 * Replaces bare "Rahu"/"Ketu" with "North Node (Rahu)"/"South Node (Ketu)".
 */
export const normalizeNodeTerms = (value: string, language: string): string => {
    if (language !== 'english' || !value) return value;
    return value
        .replace(/\bNorth Node\s*\(Rahu\)/gi, 'North Node (Rahu)')
        .replace(/\bSouth Node\s*\(Ketu\)/gi, 'South Node (Ketu)')
        .replace(/\bNorth Node\b(?!\s*\(Rahu\))/gi, 'North Node (Rahu)')
        .replace(/\bSouth Node\b(?!\s*\(Ketu\))/gi, 'South Node (Ketu)')
        .replace(/\bRahu\b/gi, 'North Node (Rahu)')
        .replace(/\bKetu\b/gi, 'South Node (Ketu)');
};
